import React, { useState } from 'react';
import {
  createPublicQuestionnaireLead,
  PublicLeadSignupInput,
} from '../../utils/publicQuestionnaireLead';
import './QuestionnaireSignupForm.css';

export interface QuestionnaireSignupFormProps {
  initialValues?: Partial<PublicLeadSignupInput>;
  onSuccess: (result: { slug: string; url: string; duplicate?: boolean }) => void;
  compact?: boolean;
}

const QuestionnaireSignupForm: React.FC<QuestionnaireSignupFormProps> = ({
  initialValues,
  onSuccess,
  compact = false,
}) => {
  const [firstName, setFirstName] = useState(initialValues?.firstName || '');
  const [lastName, setLastName] = useState(initialValues?.lastName || '');
  const [email, setEmail] = useState(initialValues?.email || '');
  const [location, setLocation] = useState(initialValues?.location || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim();
    const loc = location.trim();

    if (!fn || !ln) {
      setError('Please enter your full name.');
      return;
    }
    if (!em || !em.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }

    setSubmitting(true);
    const result = await createPublicQuestionnaireLead({
      firstName: fn,
      lastName: ln,
      email: em,
      location: loc,
    });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    onSuccess({
      slug: result.slug,
      url: result.url,
      duplicate: result.duplicate,
    });
  };

  const content = (
    <>
      {!compact && (
        <>
          <p className="qsignup-brand">LCTF Design Studio</p>
          <h1 className="qsignup-title">
            Explore <em>Timber Frame</em> Styles
          </h1>
          <p className="qsignup-lead">
            Browse wood species, frame styles, and design ideas at your own pace using our style
            guide distilled from hundreds of frames.
          </p>
        </>
      )}

      <form className="qsignup-form" onSubmit={handleSubmit}>
        <div className="qsignup-row">
          <div className="qsignup-field">
            <label className="qsignup-label" htmlFor="qsignup-first">
              First Name
            </label>
            <input
              id="qsignup-first"
              className="qsignup-input"
              type="text"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
            />
          </div>
          <div className="qsignup-field">
            <label className="qsignup-label" htmlFor="qsignup-last">
              Last Name
            </label>
            <input
              id="qsignup-last"
              className="qsignup-input"
              type="text"
              placeholder="Smith"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
            />
          </div>
        </div>

        <div className="qsignup-field">
          <label className="qsignup-label" htmlFor="qsignup-email">
            Email
          </label>
          <input
            id="qsignup-email"
            className="qsignup-input"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div className="qsignup-field">
          <label className="qsignup-label" htmlFor="qsignup-location">
            Location <span>(city, state)</span>
          </label>
          <input
            id="qsignup-location"
            className="qsignup-input"
            type="text"
            placeholder="e.g. Asheville, NC"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            autoComplete="address-level2"
          />
        </div>

        <button className="qsignup-submit" type="submit" disabled={submitting}>
          {submitting ? 'Starting...' : 'Start Exploring'}
        </button>

        {error && <p className="qsignup-error">{error}</p>}
      </form>

      {!compact && (
        <p className="qsignup-footnote">No pressure — just beautiful timber frames.</p>
      )}
    </>
  );

  if (compact) {
    return <div className="qsignup-card">{content}</div>;
  }

  return (
    <div className="qsignup-page">
      <div className="qsignup-card">{content}</div>
    </div>
  );
};

export default QuestionnaireSignupForm;
